/**
 * Browser capability report (PLAN.md §16.3). Shown in Settings → About this
 * browser and included in the self-test report. Everything here is feature
 * detection; the browser name is for display only.
 */
import { currentBrowser } from './browser';
import { currentLengthCap } from './lengthCap';
import type { OpfsProbeResult } from './opfs-probe.worker';

export type Status = 'ok' | 'warn' | 'missing' | 'info';

export interface CapItem {
  label: string;
  value: string;
  status: Status;
}

export interface CapSection {
  title: string;
  items: CapItem[];
}

export interface CapabilityReport {
  appVersion: string;
  generatedAt: string;
  userAgent: string;
  sections: CapSection[];
}

const ok = (label: string, value = 'yes'): CapItem => ({ label, value, status: 'ok' });
const missing = (label: string, value = 'no'): CapItem => ({ label, value, status: 'missing' });
const warn = (label: string, value: string): CapItem => ({ label, value, status: 'warn' });
const info = (label: string, value: string): CapItem => ({ label, value, status: 'info' });
const yesNo = (label: string, v: boolean): CapItem => (v ? ok(label) : missing(label));

function withTimeout<T>(p: Promise<T>, ms: number, fallback: T): Promise<T> {
  return Promise.race([p, new Promise<T>((r) => setTimeout(() => r(fallback), ms))]);
}

/** Tiny clips in public/probe/, generated by scripts/make-probe-clips.sh. */
export const PROBE_CLIPS: { label: string; file: string }[] = [
  { label: 'WAV (PCM)', file: 'probe.wav' },
  { label: 'MP3', file: 'probe.mp3' },
  { label: 'AAC in .m4a', file: 'probe.m4a' },
  { label: 'AAC in .mp4 (video container)', file: 'probe.mp4' },
  { label: 'FLAC', file: 'probe.flac' },
  { label: 'Ogg Vorbis', file: 'probe-vorbis.ogg' },
  { label: 'Ogg Opus', file: 'probe-opus.ogg' },
  { label: 'WebM Opus', file: 'probe.webm' },
];

async function probeDecode(file: string): Promise<boolean> {
  try {
    const res = await fetch(`${import.meta.env.BASE_URL}probe/${file}`);
    if (!res.ok) return false;
    const bytes = await res.arrayBuffer();
    const ctx = new OfflineAudioContext(1, 1, 48000);
    const buf = await withTimeout(
      ctx.decodeAudioData(bytes).catch(() => null),
      4000,
      null,
    );
    return !!buf && buf.length > 0;
  } catch {
    return false;
  }
}

function probeOpfs(): Promise<OpfsProbeResult> {
  return withTimeout(
    new Promise<OpfsProbeResult>((resolve) => {
      try {
        const w = new Worker(new URL('./opfs-probe.worker.ts', import.meta.url), {
          type: 'module',
        });
        w.onmessage = (e: MessageEvent<OpfsProbeResult>) => {
          resolve(e.data);
          w.terminate();
        };
        w.onerror = (e) => {
          resolve({ opfs: false, syncAccessHandle: false, error: e.message });
          w.terminate();
        };
      } catch (e) {
        resolve({ opfs: false, syncAccessHandle: false, error: String(e) });
      }
    }),
    5000,
    { opfs: false, syncAccessHandle: false, error: 'timeout' },
  );
}

type CodecSupportCheck = (cfg: object) => Promise<{ supported?: boolean }>;

async function webCodecsSupport(
  api: { isConfigSupported: CodecSupportCheck } | undefined,
  codecs: { label: string; codec: string }[],
): Promise<CapItem[]> {
  if (!api) return [missing('WebCodecs audio', 'not available')];
  const out: CapItem[] = [];
  for (const c of codecs) {
    try {
      const r = await api.isConfigSupported({
        codec: c.codec,
        sampleRate: 48000,
        numberOfChannels: 1,
        bitrate: 128000,
      });
      out.push(yesNo(c.label, !!r.supported));
    } catch {
      out.push(missing(c.label));
    }
  }
  return out;
}

export async function getCapabilityReport(): Promise<CapabilityReport> {
  const b = currentBrowser();
  const sections: CapSection[] = [];

  // --- Browser ---
  const nav = navigator as Navigator & {
    deviceMemory?: number;
    audioSession?: { type: string };
  };
  const cap = currentLengthCap();
  sections.push({
    title: 'Browser',
    items: [
      info('Browser', `${b.name} ${b.version}`.trim()),
      info('Platform', b.isAppleMobile ? 'iOS / iPadOS' : navigator.platform || 'unknown'),
      yesNo('Secure context (HTTPS)', window.isSecureContext),
      info(
        'Device memory',
        nav.deviceMemory !== undefined ? `${nav.deviceMemory} GB` : 'not reported',
      ),
      info('CPU threads', String(navigator.hardwareConcurrency ?? 'unknown')),
      cap.minutes < 15
        ? warn('Max file/recording length', `${cap.minutes} min (${cap.reason})`)
        : ok('Max file/recording length', `${cap.minutes} min`),
    ],
  });

  // --- Audio ---
  const audio: CapItem[] = [];
  const hasAudioContext = typeof AudioContext !== 'undefined';
  audio.push(yesNo('Web Audio (AudioContext)', hasAudioContext));
  if (hasAudioContext) {
    try {
      const ctx = new AudioContext();
      audio.push(info('Default AudioContext sample rate', `${ctx.sampleRate} Hz`));
      audio.push(
        yesNo('AudioWorklet', 'audioWorklet' in ctx && typeof AudioWorkletNode !== 'undefined'),
      );
      await ctx.close();
    } catch (e) {
      audio.push(warn('AudioContext', `could not create: ${String(e)}`));
    }
  }
  audio.push(yesNo('Microphone API (getUserMedia)', !!navigator.mediaDevices?.getUserMedia));
  audio.push(
    'audioSession' in navigator
      ? ok('Audio session API (iOS silent switch)', `yes (type: ${nav.audioSession?.type ?? '?'})`)
      : info('Audio session API (iOS silent switch)', 'not available (only needed on iOS)'),
  );
  audio.push(
    info('Mic processing flags (applied)', 'shown after the microphone is opened in Live mode'),
  );
  sections.push({ title: 'Audio', items: audio });

  // --- Storage ---
  const storage: CapItem[] = [];
  const opfs = await probeOpfs();
  storage.push(yesNo('Origin Private File System (OPFS)', opfs.opfs));
  storage.push(
    opfs.syncAccessHandle
      ? ok('OPFS sync access handle (in worker)')
      : warn(
          'OPFS sync access handle (in worker)',
          `no — recordings are buffered in memory${opfs.error ? ` (${opfs.error})` : ''}`,
        ),
  );
  if (navigator.storage?.persisted) {
    const persisted = await navigator.storage.persisted().catch(() => false);
    storage.push(
      persisted
        ? ok('Persistent storage granted')
        : info('Persistent storage granted', 'not yet (requested when you first record)'),
    );
  } else {
    storage.push(missing('Persistent storage API'));
  }
  if (navigator.storage?.estimate) {
    const est = await navigator.storage.estimate().catch(() => undefined);
    if (est?.quota) storage.push(info('Storage quota', `${(est.quota / 1e9).toFixed(1)} GB`));
  }
  sections.push({ title: 'Storage', items: storage });

  // --- Decoding ---
  const decodeResults = await Promise.all(PROBE_CLIPS.map((c) => probeDecode(c.file)));
  sections.push({
    title: 'Decodable formats (decodeAudioData)',
    items: PROBE_CLIPS.map((c, i) => yesNo(c.label, !!decodeResults[i])),
  });

  // --- Encoding ---
  const enc: CapItem[] = [];
  if (typeof MediaRecorder !== 'undefined') {
    for (const t of [
      'audio/webm;codecs=opus',
      'audio/ogg;codecs=opus',
      'audio/mp4;codecs=mp4a.40.2',
      'audio/mp4',
      'audio/wav',
    ]) {
      enc.push(yesNo(`MediaRecorder ${t}`, MediaRecorder.isTypeSupported(t)));
    }
  } else {
    enc.push(missing('MediaRecorder', 'not available'));
  }
  const g = globalThis as unknown as {
    AudioEncoder?: { isConfigSupported: CodecSupportCheck };
    AudioDecoder?: { isConfigSupported: CodecSupportCheck };
  };
  const wcCodecs = [
    { label: 'Opus', codec: 'opus' },
    { label: 'AAC-LC', codec: 'mp4a.40.2' },
    { label: 'FLAC', codec: 'flac' },
    { label: 'MP3', codec: 'mp3' },
  ];
  enc.push(
    ...(await webCodecsSupport(g.AudioEncoder, wcCodecs)).map((i) => ({
      ...i,
      label: `WebCodecs encoder: ${i.label}`,
    })),
  );
  enc.push(
    ...(
      await webCodecsSupport(
        g.AudioDecoder,
        wcCodecs.filter((c) => c.codec !== 'flac'),
      )
    ).map((i) => ({ ...i, label: `WebCodecs decoder: ${i.label}` })),
  );
  sections.push({ title: 'Encoding (browser codecs)', items: enc });

  // --- Misc ---
  sections.push({
    title: 'Other',
    items: [
      info('Cross-origin isolated', crossOriginIsolated ? 'yes' : 'no (not needed in v1)'),
      yesNo('File save dialog (showSaveFilePicker)', 'showSaveFilePicker' in window),
      info('Device pixel ratio', String(window.devicePixelRatio)),
    ],
  });

  return {
    appVersion: __APP_VERSION__,
    generatedAt: new Date().toISOString(),
    userAgent: navigator.userAgent,
    sections,
  };
}

const MARK: Record<Status, string> = { ok: '✓', warn: '!', missing: '✗', info: '·' };

export function reportToText(r: CapabilityReport): string {
  const lines = [
    `SoundLab ${r.appVersion} — capability report`,
    `Generated: ${r.generatedAt}`,
    `User agent: ${r.userAgent}`,
  ];
  for (const s of r.sections) {
    lines.push('', `## ${s.title}`);
    for (const i of s.items) lines.push(`${MARK[i.status]} ${i.label}: ${i.value}`);
  }
  return lines.join('\n');
}
